/* ──────────────────────────────────────────────────────────────
 *  BushingTypeChangeDialog — Warn before discarding compute results
 *
 *  Shown when the user changes the bushing type while generated
 *  samples from a previous compute run are still present.
 *  Confirming applies the new type (which triggers clearGeneratedSamples
 *  via useTrainingData). Cancelling leaves everything unchanged.
 * ────────────────────────────────────────────────────────────── */

import React from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import DialogShell from '@/components/DialogShell';
import { useUIStore } from '@/stores/useUIStore';

interface BushingTypeChangeDialogProps {
  /** Called when the user confirms the type change. */
  onConfirm: () => void;
}

const BushingTypeChangeDialog: React.FC<BushingTypeChangeDialogProps> = ({ onConfirm }) => {
  const open = useUIStore((s) => s.bushingTypeChangeDialogOpen);
  const closeDialog = useUIStore((s) => s.closeDialog);

  const handleCancel = () => closeDialog('bushingTypeChangeDialog');

  const handleConfirm = () => {
    closeDialog('bushingTypeChangeDialog');
    onConfirm();
  };

  return (
    <DialogShell
      open={open}
      onClose={handleCancel}
      title="Discard Results"
      titleColor="primary.main"
      icon={<WarningAmberIcon sx={{ fontSize: 35, color: 'warning.main' }} />}
      maxWidth={600}
      actions={
        <>
          <Button
            variant="contained"
            color="info"
            onClick={handleCancel}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleConfirm}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Confirm
          </Button>
        </>
      }
    >
      <Typography sx={{ fontSize: '16.5px', lineHeight: 1.8, color: '#333' }}>
        Changing the bushing type will{' '}
        <strong>discard all currently generated results</strong>, including sample
        selections and shore-scaling baselines.
      </Typography>
      <Typography sx={{ fontSize: '16.5px', lineHeight: 1.8, color: '#d32f2f', mt: 1 }}>
        This action cannot be undone. Are you sure you want to continue?
      </Typography>
    </DialogShell>
  );
};

export default BushingTypeChangeDialog;
